"use client"

import * as React from "react"
import { Building2, Globe, Briefcase, TrendingUp, Layers, ChevronDown, Search, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { useDebounce } from "@/hooks/use-debounce"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

const BRANDFETCH_CLIENT_ID = "ID_CLIENT"

const INDUSTRIES = [
    "SaaS", "Fintech", "Healthtech", "E-commerce", "AI/ML",
    "Edtech", "Web3/Crypto", "Clean Energy", "Logistics", "Consumer",
    "PropTech", "AgriTech", "Cybersecurity", "Gaming & eSports",
    "Media & Entertainment", "InsurTech", "Manufacturing", "Retail",
    "Travel & Hospitality", "Mobility & Automotive", "Biotech",
    "SpaceTech", "Human Resources", "Marketing Tech", "GovTech",
    "Social Impact", "Food & Beverage", "Robotics", "Real Estate",
    "Telecommunications", "Aerospace & Defense", "Fashion & Lifestyle",
    "Professional Services", "Construction Tech", "Hardware & IoT"
]

const STAGES = [
    "Idea Stage",
    "Pre-Seed Stage",
    "Seed Stage",
    "Series A",
    "Series B",
    "Series C",
    "Series D",
    "Series E (and beyond)",
    "Pre-IPO Stage",
    "IPO (Initial Public Offering)",
    "Public Company",
    "Acquisition / Merger / Exit",
    "Bankruptcy / Shutdown"
]

const SLIDE_COUNTS = [
    "10 Slides", "15 Slides", "18 Slides", "20 Slides", "Auto"
]

interface Brand {
    name: string
    domain: string
    icon: string | null
    brandId: string
}

interface PitchDeckHeaderProps {
    data: {
        companyName: string
        website: string
        industry: string
        stage: string
        slideCount: string
    }
    onDataChange: (field: string, value: string) => void
}

export function PitchDeckHeader({ data, onDataChange }: PitchDeckHeaderProps) {
    const [websiteSearch, setWebsiteSearch] = React.useState(data.website)
    const [brands, setBrands] = React.useState<Brand[]>([])
    const [loadingBrands, setLoadingBrands] = React.useState(false)
    const [brandPopoverOpen, setBrandPopoverOpen] = React.useState(false)

    const debouncedWebsite = useDebounce(websiteSearch, 300)

    React.useEffect(() => {
        const fetchBrands = async () => {
            if (!debouncedWebsite || debouncedWebsite.length < 2 || debouncedWebsite.includes('.')) {
                setBrands([])
                return
            }
            setLoadingBrands(true)
            try {
                const response = await fetch(
                    `https://api.brandfetch.io/v2/search/${encodeURIComponent(debouncedWebsite)}?c=${BRANDFETCH_CLIENT_ID}`
                )
                if (response.ok) {
                    const brandsData = await response.json()
                    setBrands(brandsData.slice(0, 5))
                    setBrandPopoverOpen(true)
                }
            } catch (error) {
                console.error("Failed to fetch brands", error)
            } finally {
                setLoadingBrands(false)
            }
        }
        fetchBrands()
    }, [debouncedWebsite])

    const handleWebsiteSelect = (brand: Brand) => {
        const url = `https://${brand.domain}`
        setWebsiteSearch(url)
        onDataChange("website", url)
        setBrandPopoverOpen(false)
    }

    const [industryPopoverOpen, setIndustryPopoverOpen] = React.useState(false)
    const [stagePopoverOpen, setStagePopoverOpen] = React.useState(false)
    const [slideCountPopoverOpen, setSlideCountPopoverOpen] = React.useState(false)

    const handleIndustrySelect = (industry: string) => {
        onDataChange("industry", industry)
        setIndustryPopoverOpen(false)
    }

    const handleOpenChange = (open: boolean, type: "industry" | "stage" | "slideCount") => {
        if (type === "industry") {
            setIndustryPopoverOpen(open)
        } else if (type === "stage") {
            setStagePopoverOpen(open)
        } else if (type === "slideCount") {
            setSlideCountPopoverOpen(open)
        }
    }

    return (
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-200 overflow-hidden">
            {/* Company Name */}
            <div className="flex-[1.2] min-w-0 flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white focus-within:border-blue-300 focus-within:ring-1 focus-within:ring-blue-100 transition-all">
                <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <input
                    type="text"
                    placeholder="Company Name"
                    value={data.companyName}
                    onChange={(e) => onDataChange("companyName", e.target.value)}
                    className="w-full bg-transparent border-none p-0 text-[13px] text-slate-600 placeholder:text-slate-500 focus:outline-none focus:ring-0 truncate"
                />
            </div>

            {/* Website with BrandFetch Popover */}
            <div className="flex-[1.5] min-w-0 relative">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white focus-within:border-blue-300 focus-within:ring-1 focus-within:ring-blue-100 transition-all">
                    <Globe className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <input
                        type="text"
                        placeholder="website.com"
                        value={websiteSearch}
                        onChange={(e) => {
                            setWebsiteSearch(e.target.value)
                            onDataChange("website", e.target.value)
                        }}
                        className="w-full bg-transparent border-none p-0 text-[13px] text-slate-600 placeholder:text-slate-500 focus:outline-none focus:ring-0 truncate"
                    />
                    {loadingBrands && <Loader2 className="w-3 h-3 animate-spin text-slate-300 flex-shrink-0" />}
                </div>

                <Popover open={brandPopoverOpen && brands.length > 0} onOpenChange={setBrandPopoverOpen}>
                    <PopoverTrigger asChild>
                        <div className="absolute inset-0 pointer-events-none" />
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-1 mt-1" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                        <div className="flex flex-col gap-0.5">
                            {brands.map((brand) => (
                                <button
                                    key={brand.brandId}
                                    onClick={() => handleWebsiteSelect(brand)}
                                    className="flex items-center gap-2.5 px-2.5 py-2 hover:bg-slate-50 rounded-md transition-colors text-left"
                                >
                                    {brand.icon ? (
                                        <img src={brand.icon} alt="" className="w-5 h-5 rounded object-contain border border-slate-100" />
                                    ) : (
                                        <div className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-400">
                                            {brand.name?.charAt(0)}
                                        </div>
                                    )}
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-xs font-medium text-slate-700 truncate">{brand.name}</span>
                                        <span className="text-[10px] text-slate-400 truncate">{brand.domain}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>

            {/* Industry Select */}
            <Popover open={industryPopoverOpen} onOpenChange={(open) => handleOpenChange(open, "industry")}>
                <div className="flex-1 min-w-0 relative">
                    <PopoverTrigger asChild>
                        <div className={cn(
                            "group flex items-center gap-2 px-3 py-1.5 rounded-full border bg-white transition-all h-[34px]",
                            industryPopoverOpen ? "border-blue-300 ring-1 ring-blue-100" : "border-slate-200 hover:border-slate-300"
                        )}>
                            <Briefcase className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-500 flex-shrink-0" />
                            <input
                                type="text"
                                placeholder="Industry"
                                value={data.industry}
                                onChange={(e) => onDataChange("industry", e.target.value)}
                                onFocus={() => setIndustryPopoverOpen(true)}
                                className="w-full bg-transparent border-none p-0 text-[13px] text-slate-600 placeholder:text-slate-500 focus:outline-none focus:ring-0 truncate"
                            />
                            <ChevronDown className={cn("w-3.5 h-3.5 text-slate-400 group-hover:text-slate-500 flex-shrink-0 transition-transform", industryPopoverOpen && "rotate-180")} />
                        </div>
                    </PopoverTrigger>
                </div>
                <PopoverContent 
                    className="w-52 p-0 shadow-lg border border-slate-200" 
                    align="start"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <div className="max-h-60 overflow-y-auto no-scrollbar p-1">
                        {INDUSTRIES.filter(i =>
                            !data.industry || i.toLowerCase().includes(data.industry.toLowerCase())
                        ).length > 0 ? (
                            INDUSTRIES.filter(i =>
                                !data.industry || i.toLowerCase().includes(data.industry.toLowerCase())
                            ).map((industry) => (
                                <button
                                    key={industry}
                                    onClick={() => handleIndustrySelect(industry)}
                                    className={cn(
                                        "w-full text-left px-3 py-1.5 text-xs rounded-md transition-colors mb-0.5",
                                        data.industry === industry ? "bg-blue-50 text-blue-600 font-medium" : "hover:bg-slate-50 text-slate-600"
                                    )}
                                >
                                    {industry}
                                </button>
                            ))
                        ) : (
                            <div className="px-3 py-4 text-xs text-slate-400 italic text-center">
                                Use "{data.industry}" as custom industry
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>

            {/* Stage Select */}
            <Popover open={stagePopoverOpen} onOpenChange={(open) => handleOpenChange(open, "stage")}>
                <div className="flex-1 min-w-0">
                    <PopoverTrigger asChild>
                        <button className="w-full flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-slate-300 transition-all text-left group cursor-pointer h-[34px]">
                            <TrendingUp className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-500 flex-shrink-0" />
                            <span className={cn("text-[13px] flex-1 truncate", !data.stage ? "text-slate-500" : "text-slate-600")}>
                                {data.stage || "Stage"}
                            </span>
                            <ChevronDown className={cn("w-3.5 h-3.5 text-slate-400 group-hover:text-slate-500 flex-shrink-0 transition-transform", stagePopoverOpen && "rotate-180")} />
                        </button>
                    </PopoverTrigger>
                </div>
                <PopoverContent className="w-48 p-1 shadow-lg border border-slate-200" align="start">
                    <div className="max-h-60 overflow-y-auto no-scrollbar py-1">
                        {STAGES.map((stage) => (
                            <button
                                key={stage}
                                onClick={() => {
                                    onDataChange("stage", stage)
                                    setStagePopoverOpen(false)
                                }}
                                className={cn(
                                    "w-full cursor-pointer text-left px-3 py-2 text-xs rounded-md transition-colors mb-0.5 font-medium",
                                    data.stage === stage ? "bg-blue-50 text-blue-600 font-semibold" : "hover:bg-slate-50 text-slate-600"
                                )}
                            >
                                {stage}
                            </button>
                        ))}
                    </div>
                </PopoverContent>
            </Popover>

            {/* Slide Count / Auto Select */}
            <Popover open={slideCountPopoverOpen} onOpenChange={(open) => handleOpenChange(open, "slideCount")}>
                <div className="flex-[0.8] min-w-[90px]">
                    <PopoverTrigger asChild>
                        <button className="w-full flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-slate-300 transition-all text-left group cursor-pointer h-[34px]">
                            <Layers className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-500 flex-shrink-0" />
                            <span className="text-[13px] font-medium flex-1 text-slate-600 truncate">
                                {data.slideCount}
                            </span>
                            <ChevronDown className={cn("w-3.5 h-3.5 text-slate-400 group-hover:text-slate-500 flex-shrink-0 transition-transform", slideCountPopoverOpen && "rotate-180")} />
                        </button>
                    </PopoverTrigger>
                </div>
                <PopoverContent className="w-32 p-1 shadow-lg border border-slate-200" align="start">
                    <div className="py-1">
                        {SLIDE_COUNTS.map((count) => (
                            <button
                                key={count}
                                onClick={() => {
                                    onDataChange("slideCount", count)
                                    setSlideCountPopoverOpen(false)
                                }}
                                className={cn(
                                    "w-full text-left px-3 py-2 text-xs rounded-md transition-colors mb-0.5",
                                    data.slideCount === count ? "bg-blue-50 text-blue-600 font-semibold" : "hover:bg-slate-50 text-slate-600"
                                )}
                            >
                                {count}
                            </button>
                        ))}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    )
}
